import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getDb } from './firebase';
import type { StatPanel, StatDataPoint } from '@2bforest/common';
import * as XLSX from 'xlsx';

const PANELS_COLLECTION = 'stat_panels';
const DATA_POINTS_COLLECTION = 'stat_data_points';

// ---------------------------------------------------------------------------
// Converters
// ---------------------------------------------------------------------------

function snapToPanel(snap: QueryDocumentSnapshot<DocumentData>): StatPanel {
  const d = snap.data();
  return {
    id: snap.id,
    title: d.title,
    subtitle: d.subtitle,
    category: d.category,
    order: d.order ?? 0,
    active: d.active ?? true,
    displayType: d.displayType,
    iconUrl: d.iconUrl,
    footerField1: d.footerField1,
    footerField2: d.footerField2,
    dataQuery: d.dataQuery ?? {},
  } as StatPanel;
}

function snapToDataPoint(snap: QueryDocumentSnapshot<DocumentData>): StatDataPoint {
  const d = snap.data();
  return {
    id: snap.id,
    theme: d.theme,
    variable: d.variable,
    dimension: d.dimension,
    subdimension: d.subdimension,
    category: d.category,
    subcategory1: d.subcategory1,
    subcategory2: d.subcategory2,
    subcategory3: d.subcategory3,
    year: d.year,
    value: d.value ?? null,
    unit: d.unit ?? '',
    source: d.source ?? '',
  };
}

// ---------------------------------------------------------------------------
// Panel CRUD
// ---------------------------------------------------------------------------

export async function listPanels(): Promise<StatPanel[]> {
  const q = query(
    collection(getDb(), PANELS_COLLECTION),
    orderBy('category', 'asc'),
    orderBy('order', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(snapToPanel);
}

export async function createPanel(data: Omit<StatPanel, 'id'>, id?: string): Promise<StatPanel> {
  const ref = id
    ? doc(getDb(), PANELS_COLLECTION, id)
    : doc(collection(getDb(), PANELS_COLLECTION));
  await setDoc(ref, data);
  return { ...data, id: ref.id };
}

export async function updatePanel(id: string, data: Partial<Omit<StatPanel, 'id'>>): Promise<void> {
  await updateDoc(doc(getDb(), PANELS_COLLECTION, id), data as DocumentData);
}

export async function deletePanel(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), PANELS_COLLECTION, id));
}

// ---------------------------------------------------------------------------
// Data point helpers
// ---------------------------------------------------------------------------

/**
 * Returns all distinct values per string field across all data points.
 * Used to populate the query builder dropdowns in StatPanelModal.
 */
export async function listDataPointFields(): Promise<Record<string, string[]>> {
  const q = query(collection(getDb(), DATA_POINTS_COLLECTION));
  const snap = await getDocs(q);

  const sets: Record<string, Set<string>> = {
    theme: new Set(),
    variable: new Set(),
    dimension: new Set(),
    subdimension: new Set(),
    category: new Set(),
    subcategory1: new Set(),
    subcategory2: new Set(),
    subcategory3: new Set(),
  };

  for (const d of snap.docs) {
    const data = d.data();
    for (const key of Object.keys(sets)) {
      const val = data[key];
      if (val !== undefined && val !== null && val !== '') {
        sets[key].add(String(val));
      }
    }
  }

  const result: Record<string, string[]> = {};
  for (const [key, set] of Object.entries(sets)) {
    result[key] = Array.from(set).sort();
  }
  return result;
}

/**
 * Fetches all data points for a given theme.
 * Used by the backoffice modal live preview.
 */
export async function listDataPointsByTheme(theme: string): Promise<StatDataPoint[]> {
  const q = query(collection(getDb(), DATA_POINTS_COLLECTION), orderBy('theme', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(snapToDataPoint).filter(p => p.theme === theme);
}

/**
 * Replaces ALL data points with the provided array.
 * Deletes existing documents in batches, then writes new ones in batches.
 * Firestore batch limit is 500 ops per batch.
 */
export async function bulkReplaceDataPoints(points: Omit<StatDataPoint, 'id'>[]): Promise<void> {
  const db = getDb();

  // Delete all existing
  const existing = await getDocs(collection(db, DATA_POINTS_COLLECTION));
  const deleteBatches: Array<ReturnType<typeof writeBatch>> = [];
  let batch = writeBatch(db);
  let count = 0;
  for (const snap of existing.docs) {
    batch.delete(snap.ref);
    count++;
    if (count === 499) {
      deleteBatches.push(batch);
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) deleteBatches.push(batch);
  await Promise.all(deleteBatches.map(b => b.commit()));

  // Write new data points in batches
  const writeBatches: Array<ReturnType<typeof writeBatch>> = [];
  let wb = writeBatch(db);
  let wcount = 0;
  for (const point of points) {
    const ref = doc(collection(db, DATA_POINTS_COLLECTION));
    // Strip undefined fields — Firestore rejects them
    const clean = Object.fromEntries(Object.entries(point).filter(([, v]) => v !== undefined));
    wb.set(ref, clean);
    wcount++;
    if (wcount === 499) {
      writeBatches.push(wb);
      wb = writeBatch(db);
      wcount = 0;
    }
  }
  if (wcount > 0) writeBatches.push(wb);
  await Promise.all(writeBatches.map(b => b.commit()));
}

// ---------------------------------------------------------------------------
// Excel parsing
// ---------------------------------------------------------------------------

/**
 * Parses an .xlsx file and returns an array of StatDataPoint objects
 * by reading all rows from the "Variáveis" sheet (skipping the header row).
 */
export function parseExcelFile(file: File): Promise<Omit<StatDataPoint, 'id'>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });

        // Try "Variáveis" sheet, fall back to first sheet
        const sheetName =
          workbook.SheetNames.find(n => n.includes('Vari')) ?? workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });

        // Skip header row (index 0)
        const points: Omit<StatDataPoint, 'id'>[] = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i] as (string | number | null)[];
          // Skip completely empty rows
          if (row.every(cell => cell === null || cell === '')) continue;

          const theme = String(row[0] ?? '').trim();
          const variable = String(row[1] ?? '').trim();
          const dimension = String(row[2] ?? '').trim();
          if (!theme || !variable || !dimension) {
            throw new Error(
              `Missing required fields (theme, variable, dimension) at data row ${i + 1}`
            );
          }

          const point: Omit<StatDataPoint, 'id'> = {
            theme,
            variable,
            dimension,
            subdimension: row[3] ? String(row[3]) : undefined,
            category: row[4] ? String(row[4]) : undefined,
            subcategory1: row[5] ? String(row[5]) : undefined,
            subcategory2: row[6] ? String(row[6]) : undefined,
            subcategory3: row[7] ? String(row[7]) : undefined,
            // col 8 = Período (text, not stored), col 10 = Notas (not stored)
            unit: String(row[9] ?? ''),
            year: (() => {
              const y = parseInt(String(row[11] ?? ''), 10);
              if (isNaN(y)) throw new Error(`Invalid year value "${row[11]}" at data row ${i + 1}`);
              return y;
            })(),
            value: row[12] !== null && row[12] !== '' ? Number(row[12]) : null,
            source: String(row[13] ?? ''),
          };
          points.push(point);
        }
        resolve(points);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
